import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useParams } from 'react-router-dom'
import { Form as RBForm } from 'react-bootstrap'
import Imgix from 'react-imgix'
import axios from 'axios'
import { Helmet } from 'react-helmet'
import {
  useErrorService,
  ABSpinner,
  useNotificationService,
  PdfPreviewModal,
  downloadQuestItineraryPdf,
  buildQuestItineraryPdfHtml,
} from '@abroad/components'
import { Formik } from 'formik'
import TimeSelection from '../form/TimeSelection'
import API from '../../utils/API'
import { QuestInputField } from '../form'
import {
  getItineraryLongDate,
  getItineraryNavDate,
  getItineraryTimeLength,
  UTCformatDateInPlan,
} from '../../utils/date'
import SendItineraryNotificationModal from './SendItineraryNotificationModal'
import ImageCropModal from '../common/ImageCropModal'
import QuestCopySelection from './QuestCopySelection'
import CopyItineraryModal from './CopyItineraryModal'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const normalizeDateString = (date) => UTCformatDateInPlan(date)

const mapDayFromApi = (day) => ({
  date: day.date,
  subTitle: day.subTitle || '',
  imageFileName: day.imageFileName || '',
  itineraries:
    day.itineraries && day.itineraries.length > 0
      ? day.itineraries.map((itinerary) => ({
          fromTime: itinerary.fromTime,
          toTime: itinerary.toTime,
          itinerary: itinerary.itinerary,
        }))
      : [{ fromTime: '', toTime: '', itinerary: '' }],
})

const getDaysForPdf = (days) =>
  days.map((day) => ({
    ...day,
    itineraries: day.itineraries.filter(
      (itinerary) => itinerary.itinerary && itinerary.itinerary.trim() !== '',
    ),
  }))

const hasItineraryContent = (days) =>
  days.some((day) =>
    day.itineraries.some(
      (itinerary) => itinerary.itinerary && itinerary.itinerary.trim() !== '',
    ),
  )

const generateDaysFromDateRange = (startDate, endDate, existingDays = []) => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const generatedDays = []

  const currentDate = new Date(start)
  while (currentDate <= end) {
    const dateString = normalizeDateString(currentDate)

    const existingDay = existingDays.find(
      (day) => day.date && normalizeDateString(day.date) === dateString,
    )

    if (existingDay) {
      generatedDays.push(existingDay)
    } else {
      generatedDays.push({
        date: currentDate.toISOString(),
        subTitle: '',
        imageFileName: '',
        itineraries: [{ fromTime: '', toTime: '', itinerary: '' }],
      })
    }

    currentDate.setDate(currentDate.getDate() + 1)
  }

  return generatedDays
}

const QuestsItinerary = forwardRef(
  (
    {
      questTitle,
      onSaveStateChange,
      onCompletenessChange,
      onPdfDownloadStateChange,
    },
    ref,
  ) => {
    const Error = useErrorService()
    const [uploadingIndex, setUploadingIndex] = useState(null)
    const [draggingIndex, setDraggingIndex] = useState(null)
    const { questId } = useParams()
    const imageRefs = useRef([])
    const [isLoading, setIsLoading] = useState(true)
    const [getItineraryData, setItineraryData] = useState({})
    const Notification = useNotificationService()
    const [days, setDays] = useState([
      {
        date: '',
        subTitle: '',
        imageFileName: '',
        itineraries: [{ fromTime: '', toTime: '', itinerary: '' }],
      },
    ])
    const [selectedDayIndex, setSelectedDayIndex] = useState(0)
    const uploadInProgress = useRef(false)
    const inputResetRef = useRef(null)
    const daysRef = useRef(days)
    daysRef.current = days

    const setSaveState = (state) => {
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange(state)
      }
    }
    const [showSendNotificationModal, setShowSendNotificationModal] =
      useState(false)
    const [showImageCropModal, setShowImageCropModal] = useState(false)
    const [selectedImageFile, setSelectedImageFile] = useState(null)
    const [selectedImageDayIndex, setSelectedImageDayIndex] = useState(null)
    const [pendingRemoveDayIndex, setPendingRemoveDayIndex] = useState(null)
    const [pendingRemoveEntry, setPendingRemoveEntry] = useState(null)
    const [showCopyModal, setShowCopyModal] = useState(false)
    const [selectedQuestForCopy, setSelectedQuestForCopy] = useState(null)
    const [isCopying, setIsCopying] = useState(false)
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
    const [showPreview, setShowPreview] = useState(false)
    const [previewDays, setPreviewDays] = useState([])
    const copyFormRef = useRef(null)
    const copySelectionInitialValues = React.useMemo(
      () => ({ questCopy: null }),
      [],
    )

    const clearCopySelection = () => {
      if (
        copyFormRef.current &&
        typeof copyFormRef.current.setFieldValue === 'function'
      ) {
        copyFormRef.current.setFieldValue('questCopy', null, false)
      }
    }

    const handleQuestSelection = (selectedOption) => {
      if (!selectedOption) return

      setSelectedQuestForCopy({
        value: selectedOption.value,
        label: selectedOption.label || '',
        extra: selectedOption.extra,
      })
      setShowCopyModal(true)
    }

    const handleCopySelectionCleared = () => {
      clearCopySelection()
      setSelectedQuestForCopy(null)
    }

    const handleCloseCopyModal = () => {
      if (!isCopying) {
        setShowCopyModal(false)
        setSelectedQuestForCopy(null)
        clearCopySelection()
      }
    }

    const handleConfirmCopyItinerary = async (payload) => {
      try {
        setIsCopying(true)
        setSaveState('saving')
        await API.AdminAPI.quests.changeItinerary(questId, payload)
        setShowCopyModal(false)
        setSelectedQuestForCopy(null)
        clearCopySelection()
        Notification.showNotification(
          'Itinerary copied successfully!',
          'success',
        )
        await getItinerary()
        setSaveState('saved')
      } catch (e) {
        Error.showError(e)
        setSaveState('error')
      } finally {
        setIsCopying(false)
      }
    }

    useEffect(() => {
      if (!getItineraryData) return

      const existingDays = getItineraryData.days || []

      if (getItineraryData.startDate && getItineraryData.endDate) {
        const generatedDays = generateDaysFromDateRange(
          getItineraryData.startDate,
          getItineraryData.endDate,
          existingDays,
        )
        const generatedDateStrings = new Set(
          generatedDays.map((day) => normalizeDateString(day.date)),
        )
        const extraDays = existingDays.filter(
          (day) =>
            day.date &&
            !generatedDateStrings.has(normalizeDateString(day.date)),
        )
        const mergedDays = [...extraDays, ...generatedDays].sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        )
        setDays(mergedDays.map(mapDayFromApi))
      } else if (existingDays.length > 0) {
        setDays(existingDays.map(mapDayFromApi))
      }
    }, [getItineraryData, questId])

    useEffect(() => {
      if (selectedDayIndex >= days.length) {
        setSelectedDayIndex(Math.max(0, days.length - 1))
      }
    }, [days.length, selectedDayIndex])

    // Keep Build rail itinerary note in sync (e.g. "6 days" vs "Not set").
    useEffect(() => {
      if (isLoading) return
      if (typeof onCompletenessChange !== 'function') return
      onCompletenessChange({
        itinerary: days,
        itineraryDays: days,
      })
      // onCompletenessChange is an inline parent setter — omit from deps.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, isLoading])

    const addNewRow = (dayIndex) => {
      const newItinerary = { fromTime: '', toTime: '', itinerary: '' }

      setDays((prevDays) => [
        ...prevDays.slice(0, dayIndex),
        {
          ...prevDays[dayIndex],
          itineraries: [...prevDays[dayIndex].itineraries, newItinerary],
        },
        ...prevDays.slice(dayIndex + 1),
      ])
    }

    const handleTableInputChange = (dayIndex, rowIndex, field, value) => {
      const updatedDays = [...days]
      updatedDays[dayIndex].itineraries[rowIndex][field] = value
      daysRef.current = updatedDays
      setDays(updatedDays)
    }

    const handleSubtitleChange = (dayIndex, newValue) => {
      const updatedDays = [...days]
      updatedDays[dayIndex].subTitle = newValue
      daysRef.current = updatedDays
      setDays(updatedDays)
    }

    const handleItitenary = (dayIndex, rowIndex, field) => {
      const updatedDays = [...days]
      const row = updatedDays[dayIndex].itineraries[rowIndex]
      // Still autosaves; this only flags the missing start time.
      if (!(row.fromTime || '').trim() && (row.itinerary || '').trim()) {
        row.showFromError = true
        Notification.showNotification('From Time is required.', 'danger')
      }
      daysRef.current = updatedDays
      setDays(updatedDays)
      handleBlur(updatedDays)
    }

    const handleTimeSelectionChange = (
      dayIndex,
      rowIndex,
      fromTime,
      toTime,
    ) => {
      const updatedDays = days.map((day, idx) =>
        idx === dayIndex
          ? {
              ...day,
              itineraries: day.itineraries.map((it) => ({ ...it })),
            }
          : day,
      )
      const itineraries = updatedDays[dayIndex].itineraries

      // Update the current row
      itineraries[rowIndex].fromTime = fromTime
      itineraries[rowIndex].toTime = toTime
      if (!fromTime && toTime) {
        itineraries[rowIndex].showFromError = true
        Notification.showNotification('From Time is required.', 'danger')
      }

      // If "toTime" is set and there are subsequent rows, update their fromTime
      if (toTime && rowIndex < itineraries.length - 1) {
        // Only update the immediate next row's fromTime if it's empty or less than current toTime
        const nextRow = itineraries[rowIndex + 1]
        if (!nextRow.fromTime || nextRow.fromTime < toTime) {
          nextRow.fromTime = toTime

          // If the next row had a toTime that's now invalid, clear it
          if (nextRow.toTime && nextRow.toTime <= toTime) {
            nextRow.toTime = ''
          }
        }
      }

      // Auto-populate empty rows with default 15-minute duration
      if (fromTime && toTime && rowIndex < itineraries.length - 1) {
        const nextRow = itineraries[rowIndex + 1]
        if (!nextRow.toTime && nextRow.fromTime === toTime) {
          // Calculate default end time (15 minutes after start)
          const [h, m] = toTime.split(':').map(Number)
          let endMinutes = h * 60 + m + 15

          // Don't exceed 24:00
          if (endMinutes >= 24 * 60) {
            endMinutes = 24 * 60 - 15 // Set to 23:45
          }

          const pad = (n) => n.toString().padStart(2, '0')
          const defaultToTime =
            pad(Math.floor(endMinutes / 60)) + ':' + pad(endMinutes % 60)

          nextRow.toTime = defaultToTime
        }
      }

      daysRef.current = updatedDays
      setDays(updatedDays)
    }

    const handleBlur = async (overrideDays) => {
      try {
        const snapshotDays = Array.isArray(overrideDays)
          ? overrideDays
          : daysRef.current
        const requestBody = {
          days: snapshotDays.map((day) => ({
            date: day.date,
            subTitle: day.subTitle,
            imageFileName: day.imageFileName,
            itineraries: day.itineraries
              .filter(
                (itinerary) =>
                  itinerary.fromTime ||
                  (itinerary.itinerary && itinerary.itinerary.trim() !== ''),
              )
              .map((itinerary) => ({
                fromTime: itinerary.fromTime || '',
                toTime: itinerary.toTime || '',
                itinerary: itinerary.itinerary || '',
              })),
          })),
        }
        setSaveState('saving')
        await API.AdminAPI.quests.updateItinerary(questId, requestBody)
        setSaveState('saved')
      } catch (e) {
        Error.showError(e)
        setSaveState('error')
      }
    }

    const getItinerary = async () => {
      try {
        setIsLoading(true)
        const { data } = await API.AdminAPI.quests.getItinerary(questId)
        setItineraryData(data)
        setIsLoading(false)
      } catch (e) {
        if (e?.code === 'validation_error') {
          setItineraryData({})
        } else {
          Error.showError(e)
        }
        setIsLoading(false)
      }
    }

    useEffect(() => {
      getItinerary()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questId])

    const removeRow = (dayIndex, rowIndex) => {
      setDays((prevDays) => {
        const updatedDays = prevDays.map((day, idx) => ({
          ...day,
          itineraries: day.itineraries.map((it) => ({ ...it })),
        }))
        const itineraries = updatedDays[dayIndex].itineraries

        // 1. Calculate durations for all rows BEFORE deletion
        const durations = itineraries.map((row) => {
          const [fh, fm] = row.fromTime.split(':').map(Number)
          const [th, tm] = row.toTime.split(':').map(Number)
          return th * 60 + tm - (fh * 60 + fm)
        })

        // 2. Remove the row
        itineraries.splice(rowIndex, 1)

        // 3. Shift times for all rows below the deleted one
        if (rowIndex < itineraries.length) {
          let prevTo = itineraries[rowIndex - 1]
            ? itineraries[rowIndex - 1].toTime
            : '00:00'
          for (let i = rowIndex; i < itineraries.length; i++) {
            itineraries[i].fromTime = prevTo
            const [ph, pm] = prevTo.split(':').map(Number)
            let fromMins = ph * 60 + pm
            let toMins = fromMins + durations[i + 1] // durations is from before deletion!
            if (toMins > 24 * 60) toMins = 24 * 60
            const pad = (n) => n.toString().padStart(2, '0')
            const nextTo = pad(Math.floor(toMins / 60)) + ':' + pad(toMins % 60)
            itineraries[i].toTime = nextTo
            prevTo = nextTo
          }
        }

        // Persist using the updated snapshot to avoid stale state saves
        handleBlur(updatedDays)
        return updatedDays
      })
    }

    const truncateLabel = (text) => {
      const t = String(text || '').trim()
      if (t.length > 72) return `${t.slice(0, 69)}\u2026`
      return t
    }

    const requestRemoveRow = (dayIndex, rowIndex) => {
      const entry = days[dayIndex]?.itineraries?.[rowIndex]
      const label = truncateLabel(entry?.title)
      setPendingRemoveEntry({ dayIndex, rowIndex, label })
    }

    const confirmRemoveRow = () => {
      const pending = pendingRemoveEntry
      setPendingRemoveEntry(null)
      if (!pending) return
      removeRow(pending.dayIndex, pending.rowIndex)
    }

    const pendingEntryMessage = pendingRemoveEntry?.label
      ? `\u201C${pendingRemoveEntry.label}\u201D will be removed from this day.`
      : 'This entry will be removed from this day.'

    const getMinFromTimeForRow = (dayIndex, rowIndex, daysData) => {
      if (rowIndex === 0) return null // First row has no restrictions

      const currentDay = daysData[dayIndex]
      if (!currentDay || !currentDay.itineraries) return null

      const previousRow = currentDay.itineraries[rowIndex - 1]
      return previousRow?.toTime || null
    }

    const addRowBelow = (dayIndex, rowIndex) => {
      setDays((prevDays) => {
        const updatedDays = prevDays.map((day, idx) => ({
          ...day,
          itineraries: day.itineraries.map((it) => ({ ...it })),
        }))
        const itineraries = updatedDays[dayIndex].itineraries

        // 1. Get the "to" time of the clicked row
        const prevToTime = itineraries[rowIndex].toTime || '00:00'

        // 2. Special case: if to time is 23:45, create row with both from and to times as 23:45
        if (prevToTime === '23:45') {
          // Insert new row with both from and to times set to 23:45
          itineraries.splice(rowIndex + 1, 0, {
            fromTime: '23:45',
            toTime: '23:45',
            itinerary: '',
          })
          return updatedDays
        }

        // 3. Normal case: New row: from = prevToTime, to = prevToTime + 15min
        const [h, m] = prevToTime.split(':').map(Number)
        let fromMinutes = h * 60 + m
        let toMinutes = fromMinutes + 15
        if (toMinutes > 24 * 60) toMinutes = 24 * 60
        const pad = (n) => n.toString().padStart(2, '0')
        const newFromTime =
          pad(Math.floor(fromMinutes / 60)) + ':' + pad(fromMinutes % 60)
        const newToTime =
          pad(Math.floor(toMinutes / 60)) + ':' + pad(toMinutes % 60)

        // 4. Insert the new row after the clicked row
        itineraries.splice(rowIndex + 1, 0, {
          fromTime: newFromTime,
          toTime: newToTime,
          itinerary: '',
        })

        // 5. Calculate durations for all rows after the inserted one (including the new row)
        const durations = itineraries.map((row, idx) => {
          const [fh, fm] = row.fromTime.split(':').map(Number)
          const [th, tm] = row.toTime.split(':').map(Number)
          return th * 60 + tm - (fh * 60 + fm)
        })

        // 6. Shift all subsequent rows' times to keep their durations
        let prevTo = newToTime
        for (let i = rowIndex + 2; i < itineraries.length; i++) {
          itineraries[i].fromTime = prevTo
          const [ph, pm] = prevTo.split(':').map(Number)
          let fromMins = ph * 60 + pm
          let toMins = fromMins + durations[i]
          if (toMins > 24 * 60) toMins = 24 * 60
          const nextTo = pad(Math.floor(toMins / 60)) + ':' + pad(toMins % 60)
          itineraries[i].toTime = nextTo
          prevTo = nextTo
        }

        return updatedDays
      })
    }

    const handleFileClick = (index) => {
      if (uploadInProgress.current) return
      if (imageRefs.current[index]) {
        imageRefs.current[index].value = '' // Reset before opening
        imageRefs.current[index].click()
      }
    }

    const validateImageFile = (imageFile) => {
      const allowedFormats = ['JPG', 'JPEG', 'PNG']
      const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()
      if (!allowedFormats.includes(fileExtension)) {
        Notification.showNotification(
          'Filetype must be one of [JPG, JPEG, PNG]',
          'danger',
        )
        return false
      }
      return true
    }

    const resetFileInput = () => {
      if (typeof inputResetRef.current === 'function') {
        inputResetRef.current()
      }
      inputResetRef.current = null
    }

    const handleCropModalClose = () => {
      setShowImageCropModal(false)
      setSelectedImageFile(null)
      setSelectedImageDayIndex(null)
      resetFileInput()
      uploadInProgress.current = false
    }

    const openCropModal = (index, imageFile, resetInput) => {
      if (!imageFile || uploadInProgress.current) return
      if (!validateImageFile(imageFile)) {
        if (resetInput) resetInput()
        return
      }
      inputResetRef.current = resetInput || null
      setSelectedImageDayIndex(index)
      setSelectedImageFile(imageFile)
      setShowImageCropModal(true)
    }

    const handleImageUpload = (croppedImageFile) => {
      if (
        !croppedImageFile ||
        uploadInProgress.current ||
        selectedImageDayIndex === null
      ) {
        return
      }
      uploadInProgress.current = true
      setShowImageCropModal(false)
      uploadItineraryImage(selectedImageDayIndex, croppedImageFile).finally(
        () => {
          setSelectedImageFile(null)
          setSelectedImageDayIndex(null)
          resetFileInput()
          uploadInProgress.current = false
        },
      )
    }

    const handleFileChange = (index, e) => {
      const files = e.target.files
      const target = e.target // Store reference to avoid null issues

      if (files && files.length > 0) {
        const imageFile = files[0]
        openCropModal(index, imageFile, () => {
          if (target) target.value = ''
        })
      } else {
        // If canceled, reset input and guard
        if (target) {
          target.value = ''
        }
        uploadInProgress.current = false
      }
    }

    const handleDragEnter = (index, e) => {
      e.preventDefault()
      e.stopPropagation()
      setDraggingIndex(index)
    }

    const handleDragOver = (index, e) => {
      e.preventDefault()
      e.stopPropagation()
      if (draggingIndex !== index) setDraggingIndex(index)
    }

    const handleDragLeave = (index, e) => {
      e.preventDefault()
      e.stopPropagation()
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setDraggingIndex((current) => (current === index ? null : current))
      }
    }

    const handleDrop = (index, e) => {
      e.preventDefault()
      e.stopPropagation()
      setDraggingIndex((current) => (current === index ? null : current))
      if (uploadInProgress.current) return
      const files = e.dataTransfer?.files
      if (files && files.length > 0) {
        openCropModal(index, files[0])
        e.dataTransfer.clearData()
      }
    }

    const uploadItineraryImage = async (index, imageFile) => {
      try {
        const contentType = imageFile.name
          .substr(imageFile.name.lastIndexOf('.') + 1)
          .toUpperCase()

        const { data } = await API.AdminAPI.quests.getQuestImage(
          questId,
          contentType,
        )
        setUploadingIndex(index)

        if (data) {
          let uploadImage = await axios.put(data.url.url, imageFile, {
            headers: {
              'Content-Type': data.url.contentType,
            },
          })

          if (uploadImage) {
            setDays((prevDays) => {
              const updatedDays = [...prevDays]
              updatedDays[index].imageFileName = data.fileName
              // Persist with the updated snapshot
              handleBlur(updatedDays)
              return updatedDays
            })
          }
        }
      } catch (e) {
        Error.showError(e)
      } finally {
        setTimeout(() => {
          setUploadingIndex(null)
        }, 850)
      }
    }

    const handleRemoveItineraryImage = async (dayIndex) => {
      try {
        setDays((prevDays) => {
          const updatedDays = [...prevDays]
          updatedDays[dayIndex].imageFileName = ''
          // Persist the changes
          handleBlur(updatedDays)
          return updatedDays
        })
        // Notification.showNotification('Image removed successfully!', 'success')
      } catch (e) {
        Notification.showNotification('Failed to remove image', 'danger')
      }
    }

    const hasStartAndEndDates =
      getItineraryData.startDate && getItineraryData.endDate
    const hasItineraryDays =
      getItineraryData.days && getItineraryData.days.length > 0
    const canDownloadPdf = hasItineraryContent(days)

    const buildPreviewHtml = useCallback(async () => {
      if (!previewDays?.length) {
        return ''
      }

      return buildQuestItineraryPdfHtml({
        questTitle: questTitle || '',
        days: previewDays,
      })
    }, [previewDays, questTitle])

    const handleDownloadPdf = async () => {
      const pdfDays = getDaysForPdf(days)
      if (!pdfDays.length || isDownloadingPdf) {
        return
      }

      setIsDownloadingPdf(true)
      if (typeof onPdfDownloadStateChange === 'function') {
        onPdfDownloadStateChange(true)
      }
      try {
        await downloadQuestItineraryPdf({
          questTitle: questTitle || '',
          days: pdfDays,
        })
      } catch (e) {
        Error.showError({
          message: 'Unable to download itinerary PDF. Please try again.',
        })
      } finally {
        setIsDownloadingPdf(false)
        if (typeof onPdfDownloadStateChange === 'function') {
          onPdfDownloadStateChange(false)
        }
      }
    }

    const handlePreviewPdf = () => {
      const pdfDays = getDaysForPdf(days)
      if (!pdfDays.length) {
        return
      }
      setPreviewDays(pdfDays)
      setShowPreview(true)
    }

    useImperativeHandle(ref, () => ({
      previewPdf: handlePreviewPdf,
      downloadPdf: handleDownloadPdf,
      openSendNotification: () => setShowSendNotificationModal(true),
      canDownloadPdf: () => canDownloadPdf,
    }))

    if (isLoading) {
      return (
        <div>
          <ABSpinner />
        </div>
      )
    }

    // Show empty state only when there are no dates and no saved days
    if (!hasStartAndEndDates && !hasItineraryDays) {
      return (
        <>
          <Helmet>
            <title>{`Experience-Itinerary | Abroad`}</title>
            <meta name='title' content={'Experience-Itinerary | Abroad'}></meta>
            <meta
              property='og:title'
              content={'Experience-Itinerary | Abroad'}></meta>
            <meta
              property='twitter:title'
              content={'Experience-Itinerary | Abroad'}></meta>
            <link
              rel='canonical'
              href={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/itinerary`}></link>
            <meta
              property='og:url'
              content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/itinerary`}></meta>
            <meta
              property='twitter:url'
              content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/itinerary`}></meta>
          </Helmet>
          <div>
            <div className='experience-ap-empty mt-12px'>
              <p>No itinerary days yet</p>
              <small>
                Save programme dates first. Changing that range later will ask
                you to remap existing days.
              </small>
            </div>
          </div>
        </>
      )
    }

    return (
      <>
        <Helmet>
          <title>{`Experience-Itinerary | Abroad`}</title>
          <meta name='title' content={'Experience-Itinerary | Abroad'}></meta>
          <meta
            property='og:title'
            content={'Experience-Itinerary | Abroad'}></meta>
          <meta
            property='twitter:title'
            content={'Experience-Itinerary | Abroad'}></meta>
          <link
            rel='canonical'
            href={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/itinerary`}></link>
          <meta
            property='og:url'
            content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/itinerary`}></meta>
          <meta
            property='twitter:url'
            content={`${process.env.REACT_APP_DOMAIN}/admin/experiences/${questId}/itinerary`}></meta>
        </Helmet>
        <div>
          <div className='experience-tools mb-3'>
            <Formik
              initialValues={copySelectionInitialValues}
              enableReinitialize={false}>
              {(formik) => {
                copyFormRef.current = formik
                return (
                  <QuestCopySelection
                    name='questCopy'
                    placeholder='Copy from another experience'
                    modalTitle='Copy itinerary'
                    modalLead='Bring days from another experience onto this one. Programme dates stay as they are. Day 1 copies onto Day 1, and so on.'
                    label=''
                    isDiscardLabelPadding={true}
                    currentQuestId={questId}
                    disabled={isCopying}
                    onQuestSelected={handleQuestSelection}
                    onClear={handleCopySelectionCleared}
                  />
                )
              }}
            </Formik>
          </div>

          {!hasStartAndEndDates && (
            <div className='experience-ap-empty mt-12px'>
              <p>No itinerary days yet</p>
              <small>
                Save programme dates first. Changing that range later will ask
                you to remap existing days.
              </small>
            </div>
          )}

          {days.length > 0 && (
            <div className='experience-itinerary-shell'>
              <div className='experience-itinerary-nav'>
                <div className='experience-itinerary-nav-head'>
                  Days ({days.length})
                </div>
                {days.map((day, dayIndex) => {
                  const entryCount = (day.itineraries || []).filter(
                    (row) => row.itinerary && row.itinerary.trim() !== '',
                  ).length
                  const navDate = day.date ? getItineraryNavDate(day.date) : ''
                  const navSub = [navDate, day.subTitle ? day.subTitle : null]
                    .filter(Boolean)
                    .join(' · ')
                  return (
                    <button
                      type='button'
                      key={dayIndex}
                      className={`experience-itinerary-nav-item${
                        selectedDayIndex === dayIndex ? ' sel' : ''
                      }`}
                      onClick={() => setSelectedDayIndex(dayIndex)}>
                      <span
                        className='itin-nav-copy'
                        style={{ flex: 1, minWidth: 0 }}>
                        <b>Day {dayIndex + 1}</b>
                        <small>{navSub || '—'}</small>
                      </span>
                      <span
                        className={`experience-itinerary-nav-count${
                          entryCount ? '' : ' is-empty'
                        }`}
                        aria-label={
                          entryCount === 1 ? '1 entry' : `${entryCount} entries`
                        }>
                        {entryCount}
                      </span>
                    </button>
                  )
                })}
              </div>

              {days[selectedDayIndex] && (
                <div className='experience-itinerary-editor'>
                  <div className='experience-itinerary-editor-head'>
                    <div className='experience-build-kicker'>
                      {getItineraryLongDate(days[selectedDayIndex].date)}
                    </div>
                    <h3>Day {selectedDayIndex + 1}</h3>
                  </div>
                  <div className='experience-itinerary-editor-body'>
                    {(() => {
                      const day = days[selectedDayIndex]
                      const dayIndex = selectedDayIndex
                      const hasFilledEntries = (day.itineraries || []).some(
                        (row) =>
                          (row.itinerary && row.itinerary.trim() !== '') ||
                          row.fromTime ||
                          row.toTime,
                      )
                      return (
                        <>
                          <div className='experience-itin-day-meta'>
                            <div className='experience-f'>
                              <label className='experience-f-label'>
                                Day subtitle
                              </label>
                              <QuestInputField
                                type='text'
                                name='subtitle'
                                value={day.subTitle}
                                id={`subtitleField-${dayIndex}`}
                                className='experience-f-control'
                                placeholder='Add subtitle'
                                handleBlur={handleBlur}
                                handleInputChange={(newValue) =>
                                  handleSubtitleChange(dayIndex, newValue)
                                }
                              />
                            </div>
                            <div className='experience-f'>
                              <label className='experience-f-label'>
                                Day image
                              </label>
                              <div
                                className='experience-img-slot'
                                data-ratio='2:1'>
                                <div
                                  className={`experience-img-frame${
                                    !day.imageFileName ? ' is-empty' : ''
                                  }${
                                    draggingIndex === dayIndex
                                      ? ' is-dragging'
                                      : ''
                                  }`}
                                  onDragEnter={(e) =>
                                    handleDragEnter(dayIndex, e)
                                  }
                                  onDragOver={(e) =>
                                    handleDragOver(dayIndex, e)
                                  }
                                  onDragLeave={(e) =>
                                    handleDragLeave(dayIndex, e)
                                  }
                                  onDrop={(e) => handleDrop(dayIndex, e)}
                                  onClick={() => handleFileClick(dayIndex)}>
                                  {uploadingIndex === dayIndex ? (
                                    <span
                                      className='experience-img-hover'
                                      style={{ opacity: 1 }}>
                                      Uploading…
                                    </span>
                                  ) : null}
                                  {day.imageFileName ? (
                                    <>
                                      <Imgix
                                        imgixParams={{
                                          fit: 'crop',
                                          crop: 'center',
                                          ar: '2:1',
                                        }}
                                        src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${day.imageFileName}`}
                                        attributeConfig={{
                                          src: 'data-src',
                                          srcSet: 'data-srcset',
                                          sizes: 'data-sizes',
                                        }}
                                        htmlAttributes={{
                                          src: `${process.env.REACT_APP_IMG_SOURCE}/quest/${questId}/${day.imageFileName}?ar=2:1&fit=crop&crop=center&fill=blur&blur=300&px=16&auto=format`,
                                        }}
                                        alt='Day'
                                        className={`lazyload${
                                          uploadingIndex === dayIndex
                                            ? ' itenaryImage'
                                            : ''
                                        }`}
                                        style={{ pointerEvents: 'none' }}
                                      />
                                      <span className='experience-img-hover'>
                                        {draggingIndex === dayIndex
                                          ? 'Drop image to upload'
                                          : 'Click to replace'}
                                      </span>
                                      <button
                                        type='button'
                                        className='experience-img-remove'
                                        aria-label='Remove image'
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          e.preventDefault()
                                          setPendingRemoveDayIndex(dayIndex)
                                        }}>
                                        ×
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <b>
                                        {draggingIndex === dayIndex
                                          ? 'Drop image to upload'
                                          : 'Upload image'}
                                      </b>
                                      <small>
                                        JPG, JPEG or PNG · cropped to 2:1
                                      </small>
                                    </>
                                  )}
                                  <RBForm.Control
                                    type='file'
                                    ref={(el) => {
                                      imageRefs.current[dayIndex] = el
                                    }}
                                    style={{ display: 'none' }}
                                    onChange={(e) =>
                                      handleFileChange(dayIndex, e)
                                    }
                                    tabIndex={-1}
                                    accept='image/jpg, image/jpeg, image/png'
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className='experience-field-head'>
                            <h4>Entries</h4>
                            <span className='meta'>
                              {
                                (day.itineraries || []).filter(
                                  (row) =>
                                    row.itinerary &&
                                    row.itinerary.trim() !== '',
                                ).length
                              }
                            </span>
                          </div>

                          {hasFilledEntries ||
                          (day.itineraries && day.itineraries.length > 0) ? (
                            <div className='experience-itin-rows'>
                              <div className='experience-itin-row-head'>
                                <span>From</span>
                                <span>To</span>
                                <span>Length</span>
                                <span>Title</span>
                                <span>Actions</span>
                              </div>
                              {day.itineraries.map((row, rowIndex) => (
                                <div
                                  className='experience-itin-entry'
                                  key={rowIndex}>
                                  <TimeSelection
                                    layout='split'
                                    handleBlur={handleBlur}
                                    fromTime={row.fromTime}
                                    toTime={row.toTime}
                                    fromInvalid={
                                      !!row.showFromError && !row.fromTime
                                    }
                                    minFromTime={getMinFromTimeForRow(
                                      dayIndex,
                                      rowIndex,
                                      days,
                                    )}
                                    onTimeChange={(from, to) =>
                                      handleTimeSelectionChange(
                                        dayIndex,
                                        rowIndex,
                                        from,
                                        to,
                                      )
                                    }
                                  />
                                  <div className='experience-itin-len'>
                                    <b>
                                      {getItineraryTimeLength(
                                        row.fromTime,
                                        row.toTime,
                                      )}
                                    </b>
                                  </div>
                                  <div className='experience-f'>
                                    <QuestInputField
                                      className='experience-f-control'
                                      type='text'
                                      name='itinerary'
                                      id={`itinerary-${dayIndex}-${rowIndex}`}
                                      value={row.itinerary}
                                      placeholder='What happens'
                                      handleBlur={() =>
                                        handleItitenary(
                                          dayIndex,
                                          rowIndex,
                                          'itinerary',
                                        )
                                      }
                                      handleInputChange={(newValue) =>
                                        handleTableInputChange(
                                          dayIndex,
                                          rowIndex,
                                          'itinerary',
                                          newValue,
                                        )
                                      }
                                    />
                                  </div>
                                  <div className='experience-itin-acts'>
                                    <button
                                      type='button'
                                      className='experience-add-below'
                                      title='Add row below'
                                      aria-label='Add row below'
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        addRowBelow(dayIndex, rowIndex)
                                      }}>
                                      +
                                    </button>
                                    <button
                                      type='button'
                                      className='experience-x'
                                      title='Delete entry'
                                      aria-label='Delete entry'
                                      onClick={() =>
                                        requestRemoveRow(dayIndex, rowIndex)
                                      }>
                                      <ExperienceDeleteIcon />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className='experience-zero-snapshot mb-12px'>
                              <b>No entries on this day</b>
                              <span>Add a timed block with a title.</span>
                            </div>
                          )}
                          <button
                            type='button'
                            className='btn-outline-experience experience-btn-sm'
                            onClick={() => addNewRow(dayIndex)}>
                            + Add entry
                          </button>
                        </>
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          <SendItineraryNotificationModal
            showModal={showSendNotificationModal}
            handleClose={() => setShowSendNotificationModal(false)}
            questTitle={questTitle}
            questId={questId}
          />
          <CopyItineraryModal
            show={showCopyModal}
            onHide={handleCloseCopyModal}
            selectedQuest={selectedQuestForCopy}
            currentQuestStartDate={getItineraryData?.startDate}
            currentQuestEndDate={getItineraryData?.endDate}
            onConfirmCopy={handleConfirmCopyItinerary}
            isLoading={isCopying}
          />
          <ImageCropModal
            show={showImageCropModal}
            onHide={handleCropModalClose}
            file={selectedImageFile}
            onConfirm={handleImageUpload}
            isUploading={uploadInProgress.current}
            aspect={2 / 1}
            ratioLabel='2:1'
            title='Crop Image'
          />
          <ExperienceConfirmModal
            show={pendingRemoveDayIndex != null}
            onHide={() => setPendingRemoveDayIndex(null)}
            onConfirm={() => {
              const dayIndex = pendingRemoveDayIndex
              setPendingRemoveDayIndex(null)
              if (dayIndex != null) handleRemoveItineraryImage(dayIndex)
            }}
            title='Remove this image?'
            message='The image will be cleared. You can upload another.'
            confirmLabel='Delete'
          />
          <ExperienceConfirmModal
            show={pendingRemoveEntry != null}
            onHide={() => setPendingRemoveEntry(null)}
            onConfirm={confirmRemoveRow}
            title='Delete this itinerary entry?'
            message={pendingEntryMessage}
            confirmLabel='Delete'
          />
          <PdfPreviewModal
            show={showPreview}
            onHide={() => {
              setShowPreview(false)
              setPreviewDays([])
            }}
            buildHtml={buildPreviewHtml}
            emptyMessage='No itinerary content available for preview.'
          />
        </div>
      </>
    )
  },
)

QuestsItinerary.displayName = 'QuestsItinerary'

export default QuestsItinerary
